import React, { useState } from "react";
import Button from "../components/Button";

// Firebase
import { db, auth } from "../firebase/firebaseConfig";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

// Normaliza el código
const normalizaCodigo = (raw) =>
  (raw || "").trim().replace(/\s+/g, "-").replace(/\//g, "-");

export default function RegistrarNuevoPaciente() {

  const [formData, setFormData] = useState({
    codigo: "",
    fecha: "",
    sexo: "",
    edad: "",
    peso: "",
    talla: "",
  });
  const [enviando, setEnviando] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Guarda copia local siempre
  const guardarLocal = (dato) => {
    const clave = "pacientes";
    const actuales = JSON.parse(localStorage.getItem(clave) || "[]");
    const sinDuplicados = actuales.filter((p) => p.codigo !== dato.codigo);
    localStorage.setItem(clave, JSON.stringify([...sinDuplicados, dato]));
  };

  // ⚠️ SIN getDoc: escribir directo para no disparar reglas de lectura
  const guardarEnFirestore = async (payload) => {
    const user = auth?.currentUser;
    if (!user) {
      alert("Debes iniciar sesión para guardar en la nube.");
      return { cloudSaved: false, cancelled: true };
    }

    // refresca claims por si acaso
    try { await user.getIdToken(true); } catch {}

    const codigoID = normalizaCodigo(payload.codigo);
    if (!codigoID) {
      alert("El código del paciente no puede estar vacío.");
      return { cloudSaved: false, cancelled: true };
    }

    const ref = doc(db, "pacientes", codigoID);
    console.log("[REG] intento escribir en:", `pacientes/${codigoID}`, "uid:", user.uid);

    try {
      // Intento de creación (si existiera y no eres la dueña, será un update y lo bloquearán las reglas)
      await setDoc(
        ref,
        {
          ...payload,
          codigo: codigoID,
          creadoEnServidor: serverTimestamp(),
          creadoPor: user.uid,                 // <- imprescindible para pasar reglas de create
          creadoPorEmail: user.email || null,
        },
        { merge: false } // creación “dura”: si existe y no eres dueña, caerá en permission-denied
      );
      console.log("[REG] setDoc OK");
      return { cloudSaved: true, created: true };
    } catch (err) {
      console.error("[REG] setDoc error:", err?.code, err?.message);

      if (err?.code === "permission-denied") {
        alert(
          "No se ha podido guardar en la nube (permission-denied).\n" +
          "Posibles causas:\n" +
          "• El código ya existe y pertenece a otro usuario.\n" +
          "• Estás en un proyecto Firebase distinto al de las reglas publicadas.\n\n" +
          "Prueba con otro código o revisa el proyecto/UID."
        );
        return { cloudSaved: false, cancelled: true, reason: "permission-denied" };
      }

      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviando) return;

    if (!auth?.currentUser) {
      alert("Inicia sesión para registrar pacientes en la nube.");
      return;
    }

    // Validación mínima
    for (const [, v] of Object.entries(formData)) {
      if (!v) {
        alert("⚠️ Todos los campos son obligatorios.");
        return;
      }
    }

    setEnviando(true);

    const payload = {
      ...formData,
      codigo: normalizaCodigo(formData.codigo),
      edad: Number(formData.edad),
      peso: Number(formData.peso),
      talla: Number(formData.talla),
      creadoEn: new Date().toISOString(),
    };

    try {
      // 1) Guardar local
      guardarLocal(payload);

      // 2) Guardar nube (sin lectura previa)
      let cloudMsg = "";
      const res = await guardarEnFirestore(payload);
      if (res.cloudSaved && res.created) cloudMsg = " + nube (creado)";
      else if (res.cancelled) cloudMsg = " (nube cancelada)";

      alert("✅ Paciente registrado: local" + cloudMsg + ".");

      // Mantén la fecha para agilizar nuevos registros
      setFormData({
        codigo: "",
        fecha: payload.fecha,
        sexo: "",
        edad: "",
        peso: "",
        talla: "",
      });
    } catch (err) {
      console.error("RegistrarNuevoPaciente error:", err);
      alert(`❌ Error guardando en la nube: ${err.code || ""} ${err.message || ""}`);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="p-4 max-w-xl mx-auto flex flex-col gap-4">
      <div className="flex items-center justify-center">
        <h3 className="font-bold text-center">REGISTRAR NUEVO PACIENTE</h3>
      </div>

      {/* (Eliminado DebugFirestorePanel) */}

      <div className="flex flex-col gap-4 mt-2 text-justify">
        <p>
          Esta aplicación permite a profesionales almacenar datos sensibles
          relacionados con sus usuarios, asociados únicamente a un código creado
          por el propio profesional, sin incluir datos identificativos directos como nombre, DNI o número de historia clínica.
        </p>
        <p>
          La app no es responsable de la exactitud, integridad o actualización de esta información. La gestión y veracidad de los datos recae exclusivamente en el usuario que los introduce.
        </p>
        <p>El profesional debe asegurarse de contar con el consentimiento explícito del titular o con una base legal válida.</p>
        <p>La aplicación no accede, comparte ni comercializa los datos almacenados.</p>
        <p>
          Todo el contenido permanece bajo el control exclusivo del profesional, quien se compromete a cumplir con las obligaciones legales en materia de protección de datos.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
        <div>
          <label className="font-semibold">Código paciente *</label>
          <input
            type="text"
            name="codigo"
            value={formData.codigo}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg p-2 w-full"
          />
        </div>

        <div>
          <label className="font-semibold">Fecha *</label>
          <input
            type="date"
            name="fecha"
            value={formData.fecha}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg p-2 w-full"
          />
        </div>

        <div>
          <label className="font-semibold">Sexo *</label>
          <select
            name="sexo"
            value={formData.sexo}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg p-2 w-full"
          >
            <option value="">Seleccione una opción</option>
            <option value="Femenino">Femenino</option>
            <option value="Masculino">Masculino</option>
          </select>
        </div>

        <div>
          <label className="font-semibold">Edad *</label>
          <input
            type="number"
            name="edad"
            value={formData.edad}
            onChange={handleChange}
            required
            min="0"
            className="border border-gray-300 rounded-lg p-2 w-full"
          />
        </div>

        <div>
          <label className="font-semibold">Peso (kg) *</label>
          <input
            type="number"
            name="peso"
            value={formData.peso}
            onChange={handleChange}
            required
            step="0.1"
            min="0"
            className="border border-gray-300 rounded-lg p-2 w-full"
          />
        </div>

        <div>
          <label className="font-semibold">Talla (cm) *</label>
          <input
            type="number"
            name="talla"
            value={formData.talla}
            onChange={handleChange}
            required
            step="0.1"
            min="0"
            className="border border-gray-300 rounded-lg p-2 w-full"
          />
        </div>

        <div className="flex justify-center mt-2">
          <Button
            type="submit"
            className={`px-8 py-2 ${enviando ? "opacity-60 cursor-not-allowed" : ""}`}
            disabled={enviando}
          >
            {enviando ? "Guardando..." : "Registrar paciente"}
          </Button>
        </div>
      </form>
    </div>
  );
}